/**
 * 平台档案 —— 全站唯一的平台真源。
 *
 * 为什么要有这个文件：
 * 之前平台名散落三处且互不相通 —— `BANNER_PLATFORMS` 9 个（带一行 style）、
 * `CopyStudio` 里 8 个纯字符串（零规则）、后端 0 个。
 * 结果是 `【平台】${i.platform}` 原样打印进 prompt：
 * **选「小红书」和选「Ozon」，发给模型的指令逐字相同**，只有那一行标签变了。
 * 平台变成了装饰品，于是「国内外差异 / 语言差异 / 卖点差异 / 风格差异」全部落空。
 *
 * 一个平台实际是**六件事的打包**，缺一件这个选择器就没用：
 *   语言（图内文字 / 文案语言）
 *   尺寸（主图 / 副图 / 视频）
 *   结构（这个平台要几张、分别是什么）
 *   卖点框架（排序逻辑 + 格式 + 硬限制）
 *   风格关键词（可直接注入 prompt 的包）
 *   合规禁词
 *
 * 改这里就够了：Banner / 套图 / 文案 / 专业工作流全部读它。
 */
import type { IconName } from '@/components/icons'
import { DETAIL_SPECS, type DetailSpec } from './platformDetails'

export type PlatformRegion = 'CN' | 'US' | 'EU' | 'RU' | 'SEA' | 'GLOBAL'

export type SellingPointFormat =
  | 'short-phrases' // 短句罗列，信息密度优先
  | 'scene-story' // 场景化口语，情绪优先
  | 'bullets' // 结构化要点（Amazon 五点）
  | 'hook-script' // 钩子脚本（短视频）
  | 'title-keywords' // 关键词堆叠（搜索位）

export type SellingPointFramework = {
  format: SellingPointFormat
  /** 卖点排序逻辑 —— 平台不同，先说价格还是先说功能，是两回事 */
  order: string[]
  /** 硬限制（字符数/字节数），没有就不写 */
  limit?: string
}

export type PlatformProfile = {
  id: string
  name: string
  /** 国际化分组用 */
  region: PlatformRegion
  /** UI 分组：国内 / 海外 */
  market: 'domestic' | 'overseas'
  icon?: IconName
  /** 图内文字该用什么语言 —— 这一条让「投 Amazon 出中文」变成不可能 */
  textLanguage: string
  /** 文案正文该用什么语言 */
  copyLanguage: string
  /** 主图是否允许出现文字（Amazon 主图不许，这条能救一个 listing） */
  textInImage: boolean
  sizes: { main: string; secondary: string; video: string }
  /**
   * 这个平台**列表页图集**要几张图、分别是什么。
   *
   * ⚠️ 2026-09-30「商品套图」形态下架（用户原话「这套图跟详情页重复了」）之后，
   * 这一项**当前没有任何形态在消费它** —— 它原来只喂 `buildSetPrompts`
   * 与 `structureOf`，那两处已随形态一起删除。
   *
   * 仍然保留（而不是跟着删）的理由是：它是**平台规格的事实档案**
   * （Amazon 1 主图 + 6 副图、Ozon 9 张…），`smoke-platforms` 把它当作
   * 「平台六要素齐全」的一条在断言，它不属于某条 UI 路径的私有物。
   * 删掉等于丢掉一份调研结论，而恢复「商品套图」时它就是现成的结构真源。
   */
  imageSetStructure: string[]
  sellingPointFramework: SellingPointFramework
  /** 风格关键词包，直接注入 prompt，不用每次手打 */
  styleKeywords: string[]
  /** 合规禁词：国内极限词 / 海外 restricted claims 互不通用 */
  bannedWords: string[]
  /** 是否出现在 Banner 的平台下拉里 */
  banner: boolean
  /**
   * 详情页 / A+ 的模块规格 —— 主图套图（`imageSetStructure`）之外的
   * 「点进商品后往下翻那几屏」。
   *
   * 每个平台挂一个**形态族引用**（见 config/platformDetails.ts），
   * 同族共用一套官方尺寸，所以改族等于改同族全部平台，不存在第二份拷贝。
   * 加这一项的直接原因：详情页原来把「亚马逊 A+」写死成统一 16:9，
   * 而 A+ 官方模块是 970×600 / 970×300 / 300×300 / 300×400 混排的。
   */
  detail: DetailSpec
  /**
   * 升级档详情规格（目前只有 Amazon 有：基本 A+ → 高级 A+）。
   *
   * **为什么必须是两个规格而不是一个**：两档的比例不一样 ——
   * 基本档 hero 是 970×600（1.62:1），高级档全宽是 1464×600（2.44:1），
   * 而且高级档全宽模块还要**另外一张移动图 600×450**。
   * 拿高级档的图往基本档后台塞，横向会被压掉约 1/3；
   * 反过来则是白出（后台会拒绝或大幅裁切）。
   * 所以档位是根决策 —— 页面把它放在页签外的 chip-row，用户自己选，程序不猜。
   */
  detailPremium?: DetailSpec
}

/* -------------------------------------------------------------------------- */
/* 通用禁词                                                                    */
/* -------------------------------------------------------------------------- */

const CN_BANNED = [
  '最', '第一', '国家级', '顶级', '极致', '唯一', '冠军', '王牌',
  '全网最低', '永久', '100% 有效', '包治', '根治',
]
const CN_CLAIM_BANNED = [
  '编造检测报告', '编造认证标识', '编造销量数据', '伪造用户评价',
]
const OS_CLAIM_BANNED = [
  'best seller', '#1', 'top rated', 'guaranteed cure', 'FDA approved',
  'clinically proven', '100% guaranteed', 'free shipping forever',
  '极限词直译（best/no.1/only）', '未经证实的认证与检测数据',
]

/* -------------------------------------------------------------------------- */
/* 平台档案                                                                    */
/* -------------------------------------------------------------------------- */

export const PLATFORMS: PlatformProfile[] = [
  /* ============================== 国内 ============================== */
  {
    id: 'taobao',
    name: '淘宝天猫',
    region: 'CN',
    market: 'domestic',
    icon: 'layout',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '1:1' },
    imageSetStructure: [
      '主图 · 纯白底正视',
      '主图 · 45° 侧视',
      '场景图 · 真实使用',
      '卖点图 · 一图一卖点',
      '细节图 · 材质微距',
      '规格 图 · 尺寸参数',
    ],
    sellingPointFramework: {
      format: 'short-phrases',
      order: ['价格优势', '核心功能', '材质工艺', '售后保障'],
      limit: '主图文字 ≤ 8 字；详情页模块标题 ≤ 12 字',
    },
    styleKeywords: ['电商风格', '信息密度高', '视觉热闹', '中文排版', '强对比'],
    bannedWords: [...CN_BANNED, ...CN_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.CN_VERTICAL,
  },
  {
    id: 'jd',
    name: '京东',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '1:1' },
    imageSetStructure: ['主图 · 纯白底', '参数图 · 核心规格', '场景图', '卖点图', '服务保障图'],
    sellingPointFramework: {
      format: 'bullets',
      order: ['品质背书', '核心参数', '使用体验', '服务与物流'],
    },
    styleKeywords: ['品质感', '蓝色系', '专业整洁', '参数清晰'],
    bannedWords: [...CN_BANNED, ...CN_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.CN_VERTICAL,
  },
  {
    id: 'pdd',
    name: '拼多多',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '1:1', video: '1:1' },
    imageSetStructure: ['主图 · 纯白底', '卖点图 · 大字', '场景图', '对比图 · 使用前后', '规格图'],
    sellingPointFramework: {
      format: 'short-phrases',
      order: ['低价', '量足', '实用', '包邮/售后'],
      limit: '主图文字尽量大，手机端 3 秒可读',
    },
    styleKeywords: ['热闹活泼', '高饱和色彩', '亲民感', '大字标题'],
    bannedWords: [...CN_BANNED, ...CN_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.CN_VERTICAL,
  },
  {
    id: 'douyin',
    name: '抖音小店',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '9:16' },
    imageSetStructure: ['主图 · 纯白底', '卖点图 · 大字竖排', '场景图', '细节图', '对比图'],
    sellingPointFramework: {
      format: 'hook-script',
      order: ['3 秒钩子', '痛点放大', '效果对比', '价格锚点'],
      limit: '口播稿 ≤ 60 字 / 15 秒；首帧必须出现产品或结果',
    },
    styleKeywords: ['年轻视觉冲击', '大字标题', '竖屏感', '快节奏'],
    bannedWords: [...CN_BANNED, ...CN_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.VERTICAL_34,
  },
  {
    id: 'kuaishou',
    name: '快手小店',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '9:16' },
    imageSetStructure: ['主图 · 纯白底', '实拍场景图', '卖点图', '细节图'],
    sellingPointFramework: {
      format: 'short-phrases',
      order: ['真实好用', '价格实惠', '谁适合买'],
    },
    styleKeywords: ['真实生活感', '信息表达直接', '主体清晰', '不过度修图'],
    bannedWords: [...CN_BANNED, ...CN_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.VERTICAL_34,
  },
  {
    id: 'xhs',
    name: '小红书',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '3:4', secondary: '3:4', video: '9:16' },
    imageSetStructure: [
      '封面图 · 3:4 标题留白',
      '图集 · 真实使用场景',
      '图集 · 细节特写',
      '图集 · 对比/合集',
    ],
    sellingPointFramework: {
      format: 'scene-story',
      order: ['情绪共鸣', '真实使用场景', '一句话卖点', '好物推荐'],
      limit: '标题 ≤ 20 字带情绪钩子；正文 300~500 字第一人称；话题标签 5~8 个',
    },
    styleKeywords: ['精致生活化', '柔和色调', '真实自然', '不过度精修', '手写体可选'],
    bannedWords: [...CN_BANNED, '绝对化用语', '硬广口吻'],
    banner: true,
    detail: DETAIL_SPECS.SOCIAL_VERTICAL,
  },
  {
    id: 'moments',
    name: '朋友圈（私域）',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '9:16' },
    imageSetStructure: [
      '主图 · 1:1 产品+场景',
      '日常图 · 生活化不摆拍',
      '细节图 · 实拍手感',
      '九宫格拼图素材',
    ],
    sellingPointFramework: {
      format: 'scene-story',
      order: ['使用场景代入', '真实体验', '限时/数量', '私聊行动号召'],
      limit: '正文 ≤ 3 行（手机端不折叠）；不刷屏，一天 1~2 条',
    },
    styleKeywords: ['生活化实拍', '手机感', '暖调', '不像广告', '不摆拍'],
    bannedWords: [...CN_BANNED, '微商体', '感叹号堆砌'],
    banner: false,
    detail: DETAIL_SPECS.SOCIAL_VERTICAL,
  },
  {
    id: '1688',
    name: '1688 批发',
    region: 'CN',
    market: 'domestic',
    textLanguage: '中文（简体）',
    copyLanguage: '中文（简体）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '1:1' },
    imageSetStructure: ['主图 · 纯白底', '规格图 · 尺寸/起订量', '细节图', '包装图 · 物流形态'],
    sellingPointFramework: {
      format: 'bullets',
      order: ['起订量与价格阶梯', '材质与工艺', '供货能力', '定制与打样'],
    },
    styleKeywords: ['B2B 批发风格', '工业简洁', '稳重可信', '参数优先'],
    bannedWords: [...CN_BANNED, ...CN_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.CN_VERTICAL,
  },

  /* ============================== 海外 ============================== */
  {
    id: 'amazon-us',
    name: 'Amazon 美国',
    region: 'US',
    market: 'overseas',
    textLanguage: '英语（美式）',
    copyLanguage: '英语（美式）',
    // 这条是关键：Amazon 主图不允许任何文字、logo、水印、边框
    textInImage: false,
    // 列表页 1 主图 + 6 副图，**都是方图**（副图写 3:4 是错的，Amazon 副图方形）。
    // 副图角色按「买家决策顺序」排：主图之后依次回答 6 个问题。
    sizes: { main: '1:1', secondary: '1:1', video: '16:9' },
    imageSetStructure: [
      'Main Image · 纯白底（无文字无道具无边框）',
      'Secondary 1 · 核心卖点（购买主因）',
      'Secondary 2 · 痛点场景（解决什么问题）',
      'Secondary 3 · 功能与结构（它怎么工作）',
      'Secondary 4 · 尺寸与适配（适合我的场景吗）',
      'Secondary 5 · 使用步骤（好不好用）',
      'Secondary 6 · 包装与随附（到手得到什么）',
    ],
    sellingPointFramework: {
      format: 'bullets',
      order: ['核心功能', '使用场景', '材质与规格', '兼容性', '质保与售后'],
      limit: '标题 ≤ 200 字符；五点描述每条 ≤ 500 字符；Search Terms ≤ 250 字节且不重复标题词',
    },
    styleKeywords: ['Anker 极简白底科技感', '真实产品还原', '信息克制', '无夸张特效', '棚拍光影干净'],
    bannedWords: [...OS_CLAIM_BANNED, '与竞品直接比价', 'contact us 引导', '保证疗效/效果'],
    banner: false,
    // 默认挂**基本档** —— 所有 Brand Registry 卖家都有，不会白做；
    // 有高级 A+ 资格的账号在页签外的档位 chip-row 切到 detailPremium。
    detail: DETAIL_SPECS.AMAZON_APLUS_BASIC,
    detailPremium: DETAIL_SPECS.AMAZON_APLUS_PREMIUM,
  },
  {
    id: 'amazon-eu',
    name: 'Amazon 欧洲',
    region: 'EU',
    market: 'overseas',
    textLanguage: '英语（英式）',
    copyLanguage: '英/德/法/意/西 各一版',
    textInImage: false,
    sizes: { main: '1:1', secondary: '1:1', video: '16:9' },
    imageSetStructure: [
      'Main Image · 纯白底（无文字无道具无边框）',
      'Secondary 1 · 核心卖点（购买主因）',
      'Secondary 2 · 痛点场景（解决什么问题）',
      'Secondary 3 · 功能与结构（它怎么工作）',
      'Secondary 4 · 尺寸与适配（适合我的场景吗）',
      'Secondary 5 · 使用步骤（好不好用）',
      'Secondary 6 · 包装与随附（到手得到什么）',
    ],
    sellingPointFramework: {
      format: 'bullets',
      order: ['功能', '场景', '合规与认证（真实可证）', '规格', '质保'],
      limit: '需符合 EU 合规声明；CE/WEEE 等标识只在真实持证时出现',
    },
    styleKeywords: ['克制专业', '低饱和', '真实还原', '合规优先'],
    bannedWords: [...OS_CLAIM_BANNED, '未经持证的 CE/环保标识'],
    banner: false,
    detail: DETAIL_SPECS.AMAZON_APLUS_BASIC,
    detailPremium: DETAIL_SPECS.AMAZON_APLUS_PREMIUM,
  },
  {
    id: 'ozon',
    name: 'Ozon（俄罗斯）',
    region: 'RU',
    market: 'overseas',
    textLanguage: '俄语',
    copyLanguage: '俄语',
    textInImage: true,
    sizes: { main: '3:4', secondary: '3:4', video: '9:16' },
    imageSetStructure: [
      '主图 · 3:4 白底',
      '图 2 · 场景使用',
      '图 3 · 卖点图解',
      '图 4 · 细节特写',
      '图 5 · 尺寸规格',
      '图 6 · 配件清单',
      '图 7 · 对比说明',
      '图 8 · 包装形态',
      '图 9 · 售后/质保',
    ],
    sellingPointFramework: {
      format: 'short-phrases',
      order: ['价格', '尺寸（俄区尺码偏大需标注）', '材质', '用途'],
      limit: '文案全部俄语；主图文字按品类决定（女装/童装/宠物/鞋/裤主图不要文案）',
    },
    styleKeywords: ['明亮背景', '直给信息', '俄语排版', '主体占比大'],
    bannedWords: [...OS_CLAIM_BANNED, '中文残留（俄区大忌）'],
    banner: false,
    detail: DETAIL_SPECS.VERTICAL_34,
  },
  {
    id: 'tiktok-shop',
    name: 'TikTok Shop',
    region: 'SEA',
    market: 'overseas',
    textLanguage: '英语（可按站点切当地语言）',
    copyLanguage: '英语 / 当地语言',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '9:16' },
    imageSetStructure: [
      '主图 · 1:1 白底',
      '竖版图 · 3:4 卖点',
      'UGC 场景图',
      '细节特写',
      '对比图',
    ],
    sellingPointFramework: {
      format: 'hook-script',
      order: ['3 秒钩子', '痛点', '效果证明', '价格锚点', '行动号召'],
      limit: '视频前 3 秒必须出产品或结果；竖版 9:16 全片无字，画面下方同样保持干净',
    },
    styleKeywords: ['UGC 手持感', '真实场景', '快节奏卡点', '不过度精修', '欧美网红镜面自拍/街拍可选'],
    bannedWords: [...OS_CLAIM_BANNED, '国内极限词直译'],
    banner: false,
    detail: DETAIL_SPECS.SQUARE_SET,
  },
  {
    id: 'temu',
    name: 'Temu',
    region: 'US',
    market: 'overseas',
    textLanguage: '英语（美式）',
    copyLanguage: '英语（美式）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '1:1' },
    imageSetStructure: ['主图 · 白底', '卖点图 · 大字英文', '场景图', '规格图', '对比图'],
    sellingPointFramework: {
      format: 'short-phrases',
      order: ['极致性价比', '实用功能', '数量/组合', '免运'],
      limit: '标题 ≤ 100 字符；主图文字英文、大字、手机端可读',
    },
    styleKeywords: ['高性价比感', '英文大字', '信息直给', '明亮背景'],
    bannedWords: [...OS_CLAIM_BANNED],
    banner: false,
    detail: DETAIL_SPECS.SQUARE_SET,
  },
  {
    id: 'shopee',
    name: 'Shopee（东南亚/中国台湾）',
    region: 'SEA',
    market: 'overseas',
    textLanguage: '英语 / 繁体中文（站点而定）',
    copyLanguage: '英语 / 繁中',
    textInImage: true,
    sizes: { main: '1:1', secondary: '1:1', video: '9:16' },
    imageSetStructure: ['主图 · 白底', '卖点图', '场景图', '规格图', '评价/口碑图'],
    sellingPointFramework: {
      format: 'short-phrases',
      order: ['价格', '功能', '规格', '物流时效'],
      limit: '中国台湾站点用繁体中文；东南亚站点英语为主',
    },
    styleKeywords: ['暖调生活风', '厨房功能风', '明亮', '亲和'],
    bannedWords: [...OS_CLAIM_BANNED],
    banner: false,
    detail: DETAIL_SPECS.SQUARE_SET,
  },
  {
    id: 'aliexpress',
    name: '速卖通',
    region: 'GLOBAL',
    market: 'overseas',
    textLanguage: '英语',
    copyLanguage: '英语（可多语种）',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '1:1' },
    imageSetStructure: ['主图 · 白底', '卖点图', '场景图', '规格图', '细节图'],
    sellingPointFramework: {
      format: 'bullets',
      order: ['功能', '规格', '兼容性', '物流'],
    },
    styleKeywords: ['跨境电商风格', '国际化', '多语言排版', '主体清晰'],
    bannedWords: [...OS_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.SQUARE_SET,
  },
  {
    id: 'shopify',
    name: '独立站 / DTC',
    region: 'GLOBAL',
    market: 'overseas',
    textLanguage: '英语',
    copyLanguage: '英语',
    textInImage: true,
    sizes: { main: '1:1', secondary: '3:4', video: '16:9' },
    imageSetStructure: [
      'Hero · 品牌首屏',
      'PDP · 主图',
      'PDP · 场景与生活图',
      'PDP · 功能模块',
      'A+ · 品牌故事与信任背书',
    ],
    sellingPointFramework: {
      format: 'scene-story',
      order: ['品牌主张', '核心价值', '使用场景', '信任背书', '行动号召'],
      limit: '文案 en-US；避免电商平台式的大字叫卖',
    },
    styleKeywords: ['品牌调性', '简洁高级', '留白充足', '生活方式摄影', '低饱和'],
    bannedWords: [...OS_CLAIM_BANNED],
    banner: true,
    detail: DETAIL_SPECS.DTC_MODULAR,
  },
  {
    /**
     * 社交平台也是平台 —— 它和前 16 个一样是「六件事的打包」，唯一不同的是 KPI。
     * 电商平台要「一眼看清产品」，IG / FB 要「让人想 Save」，
     * 所以图内文字要压到 15% 以内、卖点走场景叙事而不是参数罗列。
     *
     * 加它的直接原因：公司清单「社媒图 · Instagram / FB 配图」这条，
     * 之前只有国内两个渠道（小红书 / 朋友圈），海外的没处落 ——
     * 补个渠道名而不补平台档案，等于换个标签、规则一条没变。
     */
    id: 'instagram',
    name: 'Instagram / Facebook',
    region: 'US',
    market: 'overseas',
    textLanguage: '英语',
    copyLanguage: '英语',
    textInImage: true,
    sizes: { main: '4:5', secondary: '1:1', video: '9:16' },
    imageSetStructure: [
      '主图 · 成品感，构图留白',
      '场景 · 真实生活使用瞬间',
      '细节 · 材质与工艺特写',
      '氛围 · 生活方式空镜',
      'UGC · 素人实拍感',
    ],
    sellingPointFramework: {
      format: 'scene-story',
      order: ['生活场景', '情绪共鸣', '产品如何融入', '轻量行动号召'],
      limit: '图内文字 ≤ 15%、英文短句 ≤ 6 词；caption 首行 125 字符内不被折叠',
    },
    styleKeywords: ['自然光', '低饱和', '胶片颗粒可接受', 'Inspired lifestyle', '不摆拍'],
    bannedWords: [...OS_CLAIM_BANNED, '图上堆字（hard-sell 排版）'],
    banner: false,
    detail: DETAIL_SPECS.SOCIAL_VERTICAL,
  },
]

/* -------------------------------------------------------------------------- */
/* 查询与派生                                                                  */
/* -------------------------------------------------------------------------- */

export const DEFAULT_PLATFORM_ID = 'taobao'

const BY_ID = new Map(PLATFORMS.map((p) => [p.id, p]))

/** 老草稿里可能存着已经改名的 id，这里做别名兜底，避免下拉变空 */
const LEGACY_ALIAS: Record<string, string> = {
  independent: 'shopify',
  'amazon': 'amazon-us',
  'taobao/tmall': 'taobao',
  淘宝天猫: 'taobao',
  京东: 'jd',
  拼多多: 'pdd',
  抖音: 'douyin',
  小红书: 'xhs',
  1688: '1688',
  'Amazon US': 'amazon-us',
  Ozon: 'ozon',
}

export function getPlatform(id: string | null | undefined): PlatformProfile {
  if (!id) return BY_ID.get(DEFAULT_PLATFORM_ID)!
  const key = BY_ID.has(id) ? id : (LEGACY_ALIAS[id] ?? id)
  return BY_ID.get(key) ?? BY_ID.get(DEFAULT_PLATFORM_ID)!
}

export const DOMESTIC_PLATFORMS = PLATFORMS.filter((p) => p.market === 'domestic')
export const OVERSEAS_PLATFORMS = PLATFORMS.filter((p) => p.market === 'overseas')

/**
 * 平台下拉的分组（`<optgroup>`）—— **唯一真源**。
 *
 * 之前 AiSetStudio 与 CopyStudio 各自手写了一份一模一样的 `PLATFORM_GROUPS`。
 * 现在详情页也要平台下拉，各自再抄一份就是第三份：以后加平台要改三处，
 * 漏一处就是「某个页面里新平台不见了」——这种 bug 不报错，只是静默少一项。
 */
export const PLATFORM_GROUPS: { label: string; list: PlatformProfile[] }[] = [
  { label: '国内平台', list: DOMESTIC_PLATFORMS },
  { label: '海外平台', list: OVERSEAS_PLATFORMS },
]

/**
 * 把用户填的卖点，按平台框架重排 + 加上格式与硬限制。
 *
 * 这是「卖点不同」的落地：同一段卖点文字，投淘宝和投 Amazon
 * 拿到的不是同一句话换标题，而是**排序、格式、字数限制都不同**。
 *
 * `languageLabel` 是**该稿实际要用的语言**，默认取平台档案的 `copyLanguage`。
 * 为什么要有这个参数：文案页 2026-10-10 多了「输出语言」（可以把稿子翻成
 * 平台默认之外的语言）—— 不把这个覆盖传进来，提示词里会同时出现
 * 「写作用语：中文（简体）」（这一行）与「写作用语：English」（上面那行），
 * 又是一份自相矛盾的指令。**语言相关的话只能有一处真源。**
 */
export function sellingPointBlock(
  p: PlatformProfile,
  raw: string,
  languageLabel?: string,
): string[] {
  const f = p.sellingPointFramework
  const lines = [
    `· 卖点排序（按此优先级组织，先讲排在前面的）：${f.order.join(' > ')}`,
    `· 表达格式：${FORMAT_LABEL[f.format]}`,
    `· 写作用语：${languageLabel ?? p.copyLanguage}`,
  ]
  if (f.limit) lines.push(`· 硬限制：${f.limit}`)
  if (raw.trim()) lines.push(`· 我方提供的卖点原始素材（按上面规则重组，不要照抄顺序）：${raw.trim()}`)
  else lines.push('· 我方未提供卖点，请基于商品与目标人群按上面规则推断，不要编造参数')
  return lines
}

export const FORMAT_LABEL: Record<SellingPointFormat, string> = {
  'short-phrases': '短句罗列，信息密度优先，每条 ≤ 12 字',
  'scene-story': '场景化口语，第一人称，像真人分享而不是广告',
  bullets: '结构化要点列表，每条独立可读，首词点题',
  'hook-script': '脚本式：钩子 → 痛点 → 证明 → 行动号召，按时间轴排',
  'title-keywords': '关键词密集，属性词前置，无标点堆砌',
}

/** 禁词统一成一段可注入的文本；国内外禁词不通用，这里已经是按平台取好的 */
export function bannedBlock(p: PlatformProfile): string {
  return `· 该平台合规禁词与不可用宣称（违反即驳回）：${p.bannedWords.join('、')}`
}

/** 图内文字语言约束 —— 这一句让「投海外出中文」不再可能 */
export function textLanguageRule(p: PlatformProfile): string {
  return p.textInImage
    ? `· 画面文字语言必须为：${p.textLanguage}，字号层级清晰，绝不遮挡产品主体`
    : `· 画面不出现任何文字、水印、边框、logo（${p.name} 主图规则，违反会被下架）`
}

/**
 * 按平台标准结构取 N 张。
 * 超出标准张数时按同一套逻辑补（换机位 / 换场景），而不是重复同一张 ——
 * 补图用固定文案，是为了让「第 7 张」这种超纲项也有明确指令，而不是留空让模型自由发挥。
 *
 * ⚠️ 与 `imageSetStructure` 同一处境：「商品套图」2026-09-30 下架后暂时没有调用方，
 * 保留为那份档案的读取入口（理由见 `imageSetStructure` 上的说明）。
 */
export function structureOf(p: PlatformProfile, outputs: number): string[] {
  const base = p.imageSetStructure
  const n = Math.max(1, outputs)
  if (n <= base.length) return base.slice(0, n)
  const variants = ['换机位 · 侧后方', '换场景 · 户外自然光', '换细节 · 使用中特写', '换角度 · 俯视']
  const extra: string[] = []
  for (let i = base.length; i < n; i += 1) {
    extra.push(`补充图 · ${variants[(i - base.length) % variants.length]}`)
  }
  return [...base, ...extra]
}
